import { ProxyTracerProvider, trace } from '@opentelemetry/api';
import type { TracerProvider, Span as OTelSpanType } from '@opentelemetry/api';
import type { BasicTracerProvider, SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { Config } from '@packages/shared/interfaces';
import { mergeDeep } from '@packages/shared/core/common/utils/mergeDeep';
import type { SpanAttribute, StartSpanParams, StartActiveSpanParams } from '../entities';
import { CustomSpan } from '../entities';
import { ObservabilitySystem } from '../interfaces';

export class OTelObservabilitySystem implements ObservabilitySystem {
  private attributes = {};

  constructor(private readonly config: Config) {}

  setAttributes(attributes: Record<string, SpanAttribute>): ObservabilitySystem {
    this.attributes = mergeDeep(this.attributes, attributes);
    return this;
  }

  getAttributes(): Record<string, SpanAttribute> {
    return this.attributes;
  }

  setGlobalTracerProvider(provider: TracerProvider): boolean {
    return trace.setGlobalTracerProvider(provider);
  }

  // TODO prevent from calling before set global tracer
  getTracerProvider(): TracerProvider {
    return trace.getTracerProvider();
  }

  addSpanProcessor(spanProcessor: SpanProcessor) {
    const tracerProvider = this.getTracerProvider();

    if (tracerProvider instanceof ProxyTracerProvider) {
      const tracer = tracerProvider.getDelegate() as BasicTracerProvider;

      tracer.addSpanProcessor(spanProcessor);
    }
  }

  startSpan({ name, options, context }: StartSpanParams): CustomSpan {
    const tracer = trace.getTracer(this.config.observability.defaultTracerName);
    const span = tracer.startSpan(name, options, context);

    span.setAttributes(this.getAttributes());

    return new CustomSpan(span);
  }

  startActiveSpan<F extends (span: CustomSpan) => ReturnType<F>>({
    name,
    fn,
    options,
    context,
  }: StartActiveSpanParams<F>): ReturnType<F> {
    const tracer = trace.getTracer(this.config.observability.defaultTracerName);

    const wrappedFn = (rawSpan: OTelSpanType) => {
      rawSpan.setAttributes(this.getAttributes());

      return fn(new CustomSpan(rawSpan) as Parameters<F>[0]);
    };

    if (options && context) {
      return tracer.startActiveSpan(name, options, context, wrappedFn);
    }

    if (options) {
      return tracer.startActiveSpan(name, options, wrappedFn);
    }

    return tracer.startActiveSpan(name, wrappedFn);
  }
}
